<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import { trackAnalyticsInvestmentProcessStep2 } from '../../../utilities/useTracking'
import { isLegalProfile, isNaturalProfile } from '../../../utilities/investment-process/useUserProfileType'
import { notify } from '../../../utilities/useNotifications'
import { i18n } from '../../../localization'
import {
    editCompanyData,
    editPersonalData,
    navigateToNextStep,
} from '../../../utilities/account/useInvestmentProcessNavigation'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import AccountPanelPersonalMissingDataAlert from '../../account/personal/AccountPanelPersonalMissingDataAlert.vue'
import AccountPanelLegalMissingDataAlert from '../../account/legal/AccountPanelLegalMissingDataAlert.vue'
import AccountPanelLegalMissingVerificationAlert from '../../account/legal/AccountPanelLegalMissingVerificationAlert.vue'
import AccountPersonalOverviewPersonalData from '../../account/personal/overview/AccountPersonalOverviewPersonalData.vue'
import AccountLegalOverviewCompanyData from '../../account/legal/overview/AccountLegalOverviewCompanyData.vue'
import AltFgInvestmentProcessConfirmSection from './AltFgInvestmentProcessConfirmSection.vue'
import InvestmentProcessNaviationButtons from '../InvestmentProcessNaviationButtons.vue'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import AccountPanelPersonalMissingVerificationAlert from '../../account/personal/AccountPanelPersonalMissingVerificationAlert.vue'
import RightOfWithdrawalWarning from '../warning/RightOfWithdrawalWarning.vue'
import ConsequencesOfRevocationWarning from '../warning/ConsequencesOfRevocationWarning.vue'
import { getAccountLink } from '../../../utilities/useLinks'

onMounted(() => {
    trackAnalyticsInvestmentProcessStep2()
})

const store = usePersonalAccountDataState()
const legalStore = useLegalAccountDataState()
const showWarningRightOfWithdrawal = computed(() => window?.showWarningRightOfWithdrawal)
const showWarningConsequencesOfRevocation = computed(() => window?.showWarningConsequencesOfRevocation)

const { investmentAmount, altfgProfessionalInvestor, altfgLegalEntity, altfgMaximumNetIncome, altfgMaximumAssets } =
    storeToRefs(useInvestmentProcessDataState())

async function checkLegalRequirements() {
    if (
        investmentAmount.value > 5000 &&
        !altfgProfessionalInvestor.value &&
        !altfgLegalEntity.value &&
        !altfgMaximumNetIncome.value &&
        !altfgMaximumAssets.value
    ) {
        notify('Error', i18n.global.t('investment_process.please_fill_required_fields'))
    } else if (
        (isNaturalProfile() && !store.hasAllRequiredNaturalProfileAttributesForInvestment) ||
        (isLegalProfile() && !legalStore.hasAllRequiredProfileAttributesForInvestment)
    ) {
        notify('Error', i18n.global.t('verification.complete_data_description'))
    } else if (
        (isNaturalProfile() && store.isVerified === false) ||
        (isLegalProfile() && legalStore.isVerified === false)
    ) {
        notify('Error', i18n.global.t('verification.complete_verification_outstanding'))
    } else {
        navigateToNextStep()
    }
}

function goToVerificationPage() {
    window.location.href = getAccountLink() + '?menu=Verification'
}
</script>

<template>
    <div class="space-y-6 px-6 py-6">
        <AccountPanelPersonalMissingDataAlert
            v-if="isNaturalProfile() && !store.hasAllRequiredNaturalProfileAttributesForInvestment"
            class="shadow-none" />

        <AccountPanelPersonalMissingVerificationAlert
            v-if="isNaturalProfile() && !store.isVerified"
            @button-click="goToVerificationPage()" />

        <AccountPanelLegalMissingDataAlert
            v-if="isLegalProfile() && !legalStore.hasAllRequiredProfileAttributesForInvestment"
            class="shadow-none" />
        <AccountPanelLegalMissingVerificationAlert
            v-if="isLegalProfile() && !legalStore.isVerified"
            @button-click="goToVerificationPage()" />
        <h3 class="pb-3 text-base font-bold text-gray-700">{{ $t('account_settings.overview') }}</h3>

        <div
            v-if="isNaturalProfile()"
            class="overflow-hidden border border-gray-200 bg-white sm:col-span-1 sm:rounded-md">
            <AccountPersonalOverviewPersonalData @edit="editPersonalData" />
        </div>
        <div
            v-else-if="isLegalProfile()"
            class="overflow-hidden border border-gray-200 bg-white sm:col-span-1 sm:rounded-md">
            <AccountLegalOverviewCompanyData @edit="editCompanyData" />
        </div>
        <AltFgInvestmentProcessConfirmSection v-if="investmentAmount > 5000" />
        <RightOfWithdrawalWarning v-if="showWarningRightOfWithdrawal" />
        <ConsequencesOfRevocationWarning v-if="showWarningConsequencesOfRevocation" />
        <InvestmentProcessNaviationButtons :on-next-click="checkLegalRequirements" />
    </div>
</template>

<style scoped></style>
